import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import "./scheduler.scss";
import axios from "axios";
import FormRendererContext from "../../services/context/FormRendererContext";
import moment from "moment";
import Loader from "../../components/common/loader";
import SelectDropDown from "../../components/common/Select";
import OptionSelector from "../../components/common/OptionSelector";
import ScheduleAppointment from "../scheduleAppointment";
import TextField from "../../components/common/TextField";
import DateInput from "../../components/common/DateInput";
import Email from "../../components/common/Email";
import ModalBox from "../../components/common/modal";
import { Calendar, CheckCircle, Clock } from "lucide-react";
const timezoneMapper = {
  est: -5,
  cst: -6,
  mst: -7,
  pst: -8,
  akst: -9,
  hst: -10,
};

const SchedulerWidget = ({
  widget,
  handleNextScreenPage = () => {},
  handleBackScreenPage = () => {},
  setAnswerForWidget = () => {},
}) => {
  const { answerData, workflowContext, journeyConfigsGlobal } =
    useContext(FormRendererContext);
  // Extract schedulerConfig config
  const schedulerConfig = journeyConfigsGlobal?.scheduler;
  const schedulerInfo = schedulerConfig?.schedulerInfo;

  // Core state
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideSteps, setHideSteps] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Scheduler related state
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState(null);
  const [providerDetails, setProviderDetails] = useState([]);

  // Patient form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDateOfBirth] = useState(null);
  const [email, setEmail] = useState("");

  const {
    scheduler_id,
    setScheduler_id,
    provider_id,
    setProvider_id,
    reason_id,
    setReason_id,
    appointment_id,
    setAppointment_id,
    patient_id,
    setPatient_id,
    department_id,
    setDepartment_id,
    HBxFirstName,
    HBxLastName,
    HBxEmail,
    HBxDob,
    HBxReasonForVisit,
    journeyId,
    amount,
    appointmentSlotData,
    patientTZ,
    setPatientTZ,
  } = useContext(FormRendererContext);

  // Memoized values
  const schedulerId = useMemo(() => widget?.id || null, [widget?.id]);
  const selectedState = useMemo(
    () => answerData?.patientStateForScheduler?.value,
    [answerData?.patientStateForScheduler?.value]
  );

  const baseApiUrl = useMemo(() => {
    return (
      process.env?.REACT_APP_SCHEDULER_URL ||
      "https://apps-sandbox.healthblox.care/scheduler"
    );
  }, []);

  // Get dynamic provider ID (fallback to 27 if not available)
  const getProviderId = useMemo(() => {
    return provider_id || appointmentSlotData?.slot?.providerId || -1;
  }, [provider_id, appointmentSlotData?.slot?.providerId]);

  // Error handler
  const handleError = useCallback((error, context) => {
    console.error(`Error in ${context}:`, error);
    setError(`Failed to ${context}. Please try again.`);
    setIsLoading(false);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // API Functions
  const fetchDepartments = useCallback(
    async (schedId) => {
      if (!schedId) {
        console.warn("Cannot fetch departments: schedulerId is null");
        return [];
      }

      const apiUrl = `${baseApiUrl}/api/v1/providers/${schedId}/departments?providerlist=true`;

      try {
        const response = await axios.get(apiUrl);
        const { HBx } = response?.data || {};
        const { departments: deptList } = HBx || {};

        if (Array.isArray(deptList)) {
          const formattedDepartments = deptList.map((dept) => ({
            ...dept,
            value: dept.departmentId,
          }));
          return formattedDepartments;
        } else {
          console.warn("Invalid departments data received");
          return [];
        }
      } catch (error) {
        handleError(error, "fetch departments");
        return [];
      }
    },
    [baseApiUrl, handleError]
  );

  const fetchReasons = useCallback(
    async (schedId, deptId, providerId = null) => {
      if (!schedId || !deptId) {
        console.warn(
          "Cannot fetch reasons: missing schedulerId or departmentId"
        );
        return [];
      }

      const provId = providerId || getProviderId;
      const apiUrl = `${baseApiUrl}/api/v1/appointments/${schedId}/reasons?departmentId=${deptId}&providerId=${provId}`;

      try {
        const response = await axios.get(apiUrl);
        const { HBx } = response?.data || {};
        const { patientappointmentreasons } = HBx || {};

        if (Array.isArray(patientappointmentreasons)) {
          const formattedReasons = patientappointmentreasons.map((reason) => ({
            ...reason,
            id: reason.reasonid || reason.reasonId,
            label: reason.reason,
          }));
          return formattedReasons;
        } else {
          console.warn("Invalid reasons data received");
          return [];
        }
      } catch (error) {
        handleError(error, "fetch reasons");
        return [];
      }
    },
    [baseApiUrl, getProviderId, handleError]
  );

  const fetchProviderDetails = useCallback(
    async (schedId, providerId) => {
      if (!schedId || !providerId) {
        console.warn(
          "Cannot fetch provider details: missing schedulerId or providerId"
        );
        return { providerId: providerId, providerDetail: null };
      }

      const apiUrl = `${baseApiUrl}/api/v1/providers/${schedId}/id/${providerId}`;

      try {
        const response = await axios.get(apiUrl);
        const { HBx } = response?.data || {};
        return { providerId: providerId, providerDetail: HBx };
      } catch (error) {
        console.error(
          `Error fetching provider details for ${providerId}:`,
          error
        );
        return { providerId: providerId, providerDetail: null };
      }
    },
    [baseApiUrl]
  );

  const fetchAllProviders = useCallback(
    async (schedId, state) => {
      if (!schedId || !state) {
        console.warn("Cannot fetch providers: missing schedulerId or state");
        return null;
      }

      const apiUrl = `${baseApiUrl}/api/v1/providers/${schedId}/state/${state}`;

      try {
        const response = await axios.get(apiUrl);
        return response?.data;
      } catch (error) {
        handleError(error, "fetch providers");
        return null;
      }
    },
    [baseApiUrl, handleError]
  );

  const checkForUsers = useCallback(
    async (schedId, fName, lName, dateOfBirth, emailAddr) => {
      if (!schedId || !fName || !lName || !dateOfBirth || !emailAddr) {
        console.warn("Cannot check for users: missing required parameters");
        return null;
      }

      const apiUrl = `${baseApiUrl}/api/v1/patients/${schedId}?firstName=${fName}&lastName=${lName}&DOB=${encodeURIComponent(
        dateFormatter(dateOfBirth)
      )}&email=${encodeURIComponent(emailAddr)}`;

      try {
        const response = await axios.get(apiUrl);
        const { HBx } = response?.data || [];
        if (!Array.isArray(HBx) || HBx.length === 0 || HBx.length > 1) {
          console.warn("Invalid or no patient data found");
          return null;
        }
        const patientData = HBx?.[0] || {};
        return patientData?.patientId || null;
      } catch (error) {
        console.error("Error checking for users:", error);
        return null;
      }
    },
    [baseApiUrl]
  );

  // Event Handlers
  const handleOptionChange = useCallback(
    (val) => {
      clearError();
      setSelectedDepartment(val);
      setDepartment_id(val);

      // Clear reasons and providers when department changes
      setReasons([]);
      setSelectedReason(null);
      setProviderDetails([]);

      // Reset reason in context
      setReason_id(null);
    },
    [setDepartment_id, setReason_id, clearError]
  );

  const handleReasonChange = useCallback(
    async (val) => {
      if (!schedulerId || !selectedState) {
        console.warn(
          "Cannot handle reason change: missing schedulerId or selectedState"
        );
        return;
      }

      setIsLoading(true);
      clearError();
      setSelectedReason(val);
      setReason_id(val);

      try {
        const response = await fetchAllProviders(schedulerId, selectedState);
        const providers =
          workflowContext?.providerId &&
          typeof workflowContext?.providerId === "string"
            ? [workflowContext?.providerId]
            : answerData?.preferredProviderForScheduler?.value &&
              typeof answerData?.preferredProviderForScheduler?.value ===
                "string"
            ? [answerData?.preferredProviderForScheduler?.value]
            : response?.providers;

        if (Array.isArray(providers) && providers.length > 0) {
          // Fetch provider details in parallel
          const providerDetailsPromises = providers.map((providerId) =>
            fetchProviderDetails(schedulerId, providerId)
          );

          const providersList = await Promise.all(providerDetailsPromises);

          // Filter out null results
          const validProviders = providersList.filter(
            (provider) => provider?.providerDetail !== null
          );

          setProviderDetails(validProviders);
        } else {
          console.warn("No providers found for the selected state");
          setProviderDetails([]);
        }
        setCurrentStep(2);
      } catch (error) {
        handleError(error, "load providers");
        setProviderDetails([]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      schedulerId,
      selectedState,
      setReason_id,
      fetchAllProviders,
      fetchProviderDetails,
      clearError,
      handleError,
    ]
  );

  const createAppointment = useCallback(
    async (patientID, patientInfoBody) => {
      if (!schedulerId || !patientID) {
        console.warn(
          "Cannot create appointment: missing schedulerId or patientID"
        );
        return;
      }

      setIsLoading(true);
      clearError();
      const apiUrl = `${baseApiUrl}/api/v1/appointments/${schedulerId}`;

      const referralSource =
        // journeyId === "66fe0af45b3a1419fb71a944" ||
        journeyId === "673ae387413351c0414fd14c"
          ? "amerigas"
          : journeyId === "66ff54a25b3a1419fb71ad68" ||
            journeyId === "673ae4a0413351c0414fd14d"
          ? "cpapcom"
          : journeyId === "66ff6b715b3a1419fb71b380" ||
            journeyId === "673ae4a6413351c0414fd14e"
          ? "default"
          : "web";

      const bookingNote = { referralSource };
      const sanitisedPatientInfoBody = structuredClone(patientInfoBody);
      const sanitisedSlot = structuredClone(appointmentSlotData?.slot || {});

      try {
        const response = await axios.put(apiUrl, {
          appointmentId: appointment_id,
          patientId: patientID,
          bookingNote: JSON.stringify(bookingNote),
          departmentId: department_id,
          reasonId: reason_id,
          meta: {
            referralSource,
            amount,
            firstName,
            email,
            providerTZ: appointmentSlotData?.providerTimeZoneOffset || null,
            patientTZ: patientTZ,
            slotData: sanitisedSlot,
            patientInfo: sanitisedPatientInfoBody,
          },
        });

        const { HBx } = response?.data || {};
        setAnswerForWidget(HBx);
        handleNextScreenPage();
        // setCurrentStep(4);
        // setOpenModal(true);

        // setTimeout(() => {
        //   setOpenModal(false);
        //   handleNextScreenPage();
        // }, 2000);
      } catch (error) {
        handleError(error, "create appointment");
      } finally {
        setIsLoading(false);
      }
    },
    [
      schedulerId,
      baseApiUrl,
      journeyId,
      appointment_id,
      department_id,
      reason_id,
      amount,
      firstName,
      email,
      patientTZ,
      appointmentSlotData,
      setAnswerForWidget,
      handleNextScreenPage,
      clearError,
      handleError,
    ]
  );

  const handleBackClick = useCallback(() => {
    clearError();
    switch (currentStep) {
      case 0:
        handleBackScreenPage();
        break;
      case 1:
        if (hideSteps.includes(0)) {
          handleBackScreenPage();
        } else {
          setCurrentStep(0);
        }
        break;
      case 2:
        if (hideSteps.includes(1)) {
          if (hideSteps.includes(0)) {
            handleBackScreenPage();
          } else {
            setCurrentStep(0);
          }
        } else {
          setCurrentStep(1);
        }
        break;
      case 3:
        setCurrentStep(2);
        break;
      default:
        handleBackScreenPage();
    }
  }, [currentStep, hideSteps, handleBackScreenPage, clearError]);

  const handleNextClick = useCallback(async () => {
    clearError();

    if (currentStep === 0 && selectedDepartment) {
      setIsLoading(true);
      try {
        const fetchedReasons = await fetchReasons(
          schedulerId,
          selectedDepartment
        );
        setReasons(fetchedReasons);
        setCurrentStep(1);
      } catch (error) {
        handleError(error, "fetch reasons");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (currentStep === 2) {
      setCurrentStep(3);
      return;
    }

    if (currentStep === 3) {
      if (!firstName || !lastName || !dob || !email) {
        setError("Please fill in all required fields");
        return;
      }

      const patientInfoBody = {
        address: {
          streetAddress: answerData?.address1?.value || "",
          ZIP: answerData?.zipCode?.value || "",
          city: answerData?.city?.value || "",
          state: answerData?.patientStateForScheduler?.value || "",
          country: answerData?.country?.value || "",
        },
        email: [email],
        phoneNumber: {
          mobile: answerData?.mobilePhone?.value || "",
        },
        consentToCall: Boolean(answerData?.consentToCall?.value),
        consentToText: Boolean(
          answerData?.iAuthorizeHBxSleepToSendTextReminders?.value
        ),
        sex:
          answerData?.gender?.value === "female"
            ? "F"
            : answerData?.gender?.value === "male"
            ? "M"
            : "",
      };

      setIsLoading(true);

      try {
        if (patient_id) {
          // Update existing patient
          const updateUrl = `${baseApiUrl}/api/v1/patients/${schedulerId}`;
          await axios.put(updateUrl, {
            patientId: patient_id,
            ...patientInfoBody,
          });
          await createAppointment(patient_id, patientInfoBody);
        } else {
          // Check if patient exists
          const existingPatientId = await checkForUsers(
            schedulerId,
            firstName,
            lastName,
            dob,
            email
          );

          if (existingPatientId) {
            // Update existing patient
            const updateUrl = `${baseApiUrl}/api/v1/patients/${schedulerId}`;
            await axios.put(updateUrl, {
              patientId: existingPatientId,
              ...patientInfoBody,
            });
            setPatient_id(existingPatientId);
            await createAppointment(existingPatientId, patientInfoBody);
          } else {
            // Create new patient
            const createUrl = `${baseApiUrl}/api/v1/patients/${schedulerId}`;
            const response = await axios.post(createUrl, {
              firstName,
              lastName,
              DOB: dateFormatter(dob),
              departmentId: department_id,
              providerId: getProviderId,
              email: [email],
              ...patientInfoBody,
            });

            const { HBx } = response?.data || {};
            const { patientId } = HBx || {};

            if (patientId) {
              setPatient_id(patientId);
              await createAppointment(patientId, patientInfoBody);
            } else {
              throw new Error(
                "Failed to create patient - no patientId returned"
              );
            }
          }
        }
      } catch (error) {
        handleError(error, "process patient information");
      }
    }
  }, [
    currentStep,
    selectedDepartment,
    firstName,
    lastName,
    dob,
    email,
    schedulerId,
    fetchReasons,
    answerData,
    patient_id,
    baseApiUrl,
    department_id,
    checkForUsers,
    setPatient_id,
    createAppointment,
    clearError,
    handleError,
  ]);

  // Utility Functions
  const dateFormatter = useCallback((date) => {
    if (!date) return "";

    try {
      if (date.$d) {
        return moment(date.$d).format("MM/DD/YYYY");
      } else {
        return moment(date).format("MM/DD/YYYY");
      }
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  }, []);

  // Form field handlers
  const setFirstNameHandler = useCallback(
    (name, data) => {
      setFirstName(data?.value || "");
      clearError();
    },
    [clearError]
  );

  const setLastNameHandler = useCallback(
    (name, data) => {
      setLastName(data?.value || "");
      clearError();
    },
    [clearError]
  );

  const setDOBHandler = useCallback(
    (name, data) => {
      setDateOfBirth(data?.value?.date || null);
      clearError();
    },
    [clearError]
  );

  const setEmailHandler = useCallback(
    (name, data) => {
      setEmail(data?.value || "");
      clearError();
    },
    [clearError]
  );
  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top when form page changes
  }, [currentStep]);

  // Single consolidated initialization effect
  useEffect(() => {
    if (!schedulerId || isInitialized) return;
    if (workflowContext?.patientId) {
      setPatient_id(workflowContext.patientId);
    }

    const initializeScheduler = async () => {
      try {
        setIsLoading(true);
        clearError();

        // Set scheduler ID in context
        setScheduler_id(schedulerId);

        // Initialize form fields from context
        if (HBxDob) setDateOfBirth(HBxDob);
        if (HBxEmail) setEmail(HBxEmail);
        if (HBxFirstName) setFirstName(HBxFirstName);
        if (HBxLastName) setLastName(HBxLastName);

        // Set timezone if available
        if (workflowContext?.timezone) {
          setPatientTZ(timezoneMapper[workflowContext.timezone]);
        }

        // Get pre-filled values
        const departmentId =
          workflowContext?.departmentId ||
          department_id ||
          answerData?.patientDepartmentForSchedulerForSpecificForm?.value;
        const reasonId =
          workflowContext?.reasonId ||
          answerData?.reasonForVisitForScheduler?.value;

        if (departmentId) {
          // Pre-filled department scenario
          setSelectedDepartment(departmentId);
          setDepartment_id(departmentId);
          setHideSteps((prev) => (prev.includes(0) ? prev : [...prev, 0]));

          // Fetch reasons for this department
          const fetchedReasons = await fetchReasons(schedulerId, departmentId);
          setReasons(fetchedReasons);

          if (reasonId) {
            // Pre-fill reason and move to provider selection
            const reasonIdInt = parseInt(reasonId, 10);

            if (reasonIdInt) {
              setSelectedReason(reasonIdInt);
              setReason_id(reasonIdInt);
              setHideSteps((prev) => (prev.includes(1) ? prev : [...prev, 1]));

              // Move directly to provider selection
              await handleReasonChange(reasonIdInt);
            } else {
              // Reason doesn't exist, show reason selection
              setCurrentStep(1);
            }
          } else {
            // No pre-filled reason, show reason selection
            setCurrentStep(1);
          }
        } else {
          // No pre-filled department, fetch departments list
          const fetchedDepartments = await fetchDepartments(schedulerId);
          setDepartments(fetchedDepartments);
          setCurrentStep(0);
        }

        setIsInitialized(true);
      } catch (error) {
        handleError(error, "initialize scheduler");
      } finally {
        setIsLoading(false);
      }
    };

    initializeScheduler();
  }, [schedulerId, workflowContext]);

  // Render helpers
  const renderError = () => {
    if (!error) return null;
    return (
      <div className="content-width p-5">
        <div className="w-full form-width mx-auto">
          <div className="px-4 py-3 bg-red-100 border border-red-400 text-red-700 rounded mb-4">
            {error}
            <button
              onClick={clearError}
              className="ml-2 text-red-900 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDepartmentSelection = () => (
    <div className="content-width p-5">
      <div className="w-full form-width mx-auto">
        <div className="form-title">Select Department</div>
        <div className="radio-common">
          <SelectDropDown
            placeHolder="Please select an option"
            options={departments}
            setOption={handleOptionChange}
            answerValue={selectedDepartment}
          />
        </div>
      </div>
    </div>
  );

  const renderReasonSelection = () => {
    if (!reasons.length && !isLoading) {
      return (
        <div className="content-width p-5">
          <div className="form-width mx-auto">
            <div className="form-title">Choose the reason for visit</div>
            <div className="text-center py-4">
              <div className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded">
                No reasons available for the selected department. Please try a
                different department.
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="content-width p-5">
        <div className="form-width mx-auto">
          <div className="form-title">Choose the reason for visit</div>
          {reasons.length > 0 ? (
            <OptionSelector
              data={{ values: reasons }}
              setOption={handleReasonChange}
            />
          ) : (
            <div className="text-center py-4">
              <div className="text-gray-500">Loading reasons...</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProviderSelection = () => {
    if (!providerDetails?.length) {
      return (
        <div className="text-xl text-center">
          <div className="px-10 inline-block py-3 rounded bg-gray-200 my-5">
            No providers found for this state
          </div>
        </div>
      );
    }

    return (
      <div>
        {schedulerInfo && schedulerInfo !== "" && (
          <div dangerouslySetInnerHTML={{ __html: schedulerInfo }} />
        )}
        <ScheduleAppointment
          data={providerDetails}
          activateNextClick={handleNextClick}
        />
      </div>
    );
  };

  const renderPatientForm = () => {
    const { slot } = appointmentSlotData;
    // Convert to 12-hour format
    const convertTo12HourFormat = (time24) => {
      if (!time24) return "";
      const [hours, minutes] = time24.split(":");
      const hour24 = parseInt(hours, 10);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const period = hour24 >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${period}`;
    };

    const getUserFriendlyTimezone = (offset) => {
      const timezoneMap = {
        "-5": "Eastern Time (ET)",
        "-6": "Central Time (CT)",
        "-7": "Mountain Time (MT)",
        "-8": "Pacific Time (PT)",
        "-9": "Alaska Time (AKT)",
        "-10": "Hawaii Time (HST)",
      };
      return (
        timezoneMap[offset.toString()] ||
        `UTC${offset >= 0 ? "+" : ""}${offset}`
      );
    };
    // Format date for display
    const formatAppointmentDate = (dateString) => {
      const [month, day, year] = dateString.split("/");
      const date = new Date(year, month - 1, day);
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      return date.toLocaleDateString("en-US", options);
    };
    return (
      <div className="content-width p-10">
        <div className="text-center mb-6">
          {/* <div className="flex justify-center mb-4">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div> */}
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Details Confirmation
          </h1>
          <p className="text-gray-600">
            Please review your appointment details below
          </p>
        </div>
        <div className=" flex flex-col bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-2xl gap-6">
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-xs text-gray-600 uppercase tracking-wide">
                Date
              </p>
              <p className="font-semibold text-gray-800">
                {formatAppointmentDate(slot.date)}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Clock className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-xs text-gray-600 uppercase tracking-wide">
                Time
              </p>
              <p className="font-semibold text-gray-800">
                {convertTo12HourFormat(slot.patientStartTime)}
                 {/* - {" "}
                {convertTo12HourFormat(slot.endTime)} */}
              </p>
              <p className="text-xs text-gray-500">
                {getUserFriendlyTimezone(
                  appointmentSlotData.providerTimeZoneOffset
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200 my-6"></div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Patient Information
        </h3>
        <div className="form-width mx-auto">
          <TextField
            data={{
              label: "First Name",
              answer: { value: firstName },
              disabled: true,
            }}
            setAnswer={setFirstNameHandler}
          />
          <TextField
            data={{
              label: "Last Name",
              answer: { value: lastName },
              disabled: true,
            }}
            setAnswer={setLastNameHandler}
          />
          <DateInput
            data={{ label: "DOB", answer: { value: dob }, disabled: true }}
            setAnswer={setDOBHandler}
          />
          <Email
            data={{ label: "Email", answer: { value: email }, disabled: true }}
            setAnswer={setEmailHandler}
          />
        </div>
      </div>
    );
  };

  const renderSuccessModal = () => (
    <ModalBox
      openModal={true}
      leftButtonText="Close"
      rightButtonText="Continue"
      showButtons={false}
      rightButtonCallback={() => {
        setOpenModal(false);
        handleNextScreenPage();
      }}
      title="Thank You !"
      SubTitle="Your Appointment has been Scheduled successfully"
      hideClose={true}
    />
  );

  const renderBottomBar = () => (
    <div className="bottom-bar-shadow bottom-bar-scheduler-shadow">
      <div className="flex justify-between pt-3 m-auto bottom-bar-scheduler w-full">
        <button onClick={handleBackClick} className="text_button">
          Back
        </button>
        <div className={currentStep === 1 || currentStep === 2 ? "hidden" : ""}>
          <div onClick={handleNextClick} className="primary_button">
            {currentStep === 3 ? "Schedule Appointment" : "Continue"}
          </div>
        </div>
      </div>
    </div>
  );

  // Main render
  if (isLoading) {
    return (
      <div>
        <div className="mt-20">
          <Loader />
        </div>
        {renderBottomBar()}
      </div>
    );
  }

  return (
    <div className="">
      {renderError()}
      {currentStep === 0 && renderDepartmentSelection()}
      {currentStep === 1 && renderReasonSelection()}
      {currentStep === 2 && renderProviderSelection()}
      {currentStep === 3 && renderPatientForm()}
      {currentStep === 4 && openModal && renderSuccessModal()}
      {renderBottomBar()}
    </div>
  );
};

export default SchedulerWidget;
